import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Link,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { PhoneIphone, ArrowBack } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { EasyCheckButton } from '../../common/Controls';
import { mobileAuthService } from '../../../services/mobile/mobileAuthService';

const OtpContainer = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #3b82f6 100%)',
  padding: '16px'
}));

const OtpCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.97)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  padding: theme.spacing(4),
  boxShadow: '0 20px 60px rgba(37, 99, 235, 0.3)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  maxWidth: '420px',
  width: '100%'
}));

const OtpInput = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontSize: '2rem',
    fontWeight: 700,
    letterSpacing: '0.5em',
    textAlign: 'center',
    height: 64,
    '& input': { textAlign: 'center' }
  }
}));

const MobileOtpVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const { email, password } = location.state || {};

  const [otp, setOtp] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    if (!email) {
      navigate('/mobile/login');
    }
    inputRef.current?.focus();
  }, [email, navigate]);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError('Please enter the 6-digit OTP.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const result = await mobileAuthService.verifyOtp({ email, password, otp, trustDevice });
      if (result.success) {
        navigate('/mobile');
      } else {
        setError(result.message || 'Invalid OTP.');
      }
    } catch {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError('');
    try {
      const result = await mobileAuthService.resendOtp(email);
      if (result.success) {
        setSuccess('OTP resent successfully.');
        setResendCountdown(60);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Failed to resend OTP.');
      }
    } catch {
      setError('Failed to resend OTP.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
  };

  return (
    <OtpContainer>
      <OtpCard>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <PhoneIphone sx={{ fontSize: 48, color: '#2563eb', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Verify OTP
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Enter the code sent to {email}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

        <form onSubmit={handleVerify}>
          <OtpInput
            fullWidth
            value={otp}
            onChange={handleOtpChange}
            inputRef={inputRef}
            inputMode="numeric"
            placeholder="000000"
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                sx={{ color: '#2563eb', '&.Mui-checked': { color: '#2563eb' } }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#475569' }}>
                Trust this device for 30 days
              </Typography>
            }
            sx={{ mb: 2, ml: 0 }}
          />

          <EasyCheckButton
            type="submit"
            fullWidth
            disabled={loading || otp.length !== 6}
            sx={{
              py: 1.75,
              borderRadius: 2,
              backgroundColor: '#2563eb',
              '&:hover': { backgroundColor: '#1d4ed8' },
              fontWeight: 600
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
          </EasyCheckButton>
        </form>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          {resendCountdown > 0 ? (
            <Typography variant="body2" color="text.secondary">
              Resend in {resendCountdown}s
            </Typography>
          ) : (
            <Link
              component="button"
              onClick={handleResend}
              disabled={resendLoading}
              sx={{ color: '#2563eb', cursor: 'pointer' }}
            >
              {resendLoading ? 'Sending...' : 'Resend OTP'}
            </Link>
          )}
        </Box>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link
            component="button"
            onClick={() => navigate('/mobile/login')}
            sx={{ color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
          >
            <ArrowBack fontSize="small" /> Back to login
          </Link>
        </Box>
      </OtpCard>
    </OtpContainer>
  );
};

export default MobileOtpVerify;
